<template>
  <div>
    <div class="filter-tags">
      <span
        class="news-type-tag filter-tag"
        :class="{ active: selectedType === null }"
        @click="selectedType = null"
      >すべて</span>
      <span
        v-for="type in availableTypes"
        :key="type"
        class="news-type-tag filter-tag"
        :class="{ active: selectedType === type }"
        :style="{
          backgroundColor: getNewsTypeColor(type).background,
          color: getNewsTypeColor(type).color,
          borderColor: getNewsTypeColor(type).border,
        }"
        @click="toggleFilter(type)"
      >{{ type }}</span>
    </div>
    <div v-if="selectedType && visibleNewsList.length === 0" class="no-news">該当するお知らせはありません。</div>
    <div v-for="(news, index) in visibleNewsList" :key="index" class="news-item" v-fade-in>
      <div class="title-date">
        <div class="date-tag-row">
          <p><small>{{ news.date }}</small></p>
          <span
            class="news-type-tag"
            :style="{
              backgroundColor: getNewsTypeColor(news.type).background,
              color: getNewsTypeColor(news.type).color,
              borderColor: getNewsTypeColor(news.type).border,
            }"
          >{{ news.type || 'その他' }}</span>
        </div>
        <h3 @click="selectNews(news)">{{ news.title }}</h3>
      </div>
    </div>
    <button v-if="hasMoreNews" @click="showMore" class="load-more">
      {{ isLoadingMore ? '読み込み中...' : 'さらに表示' }}
    </button>
  </div>
</template>

<script>
import { NEWS_TYPES, DEFAULT_NEWS_TYPE, getNewsTypeColor } from '@/utils/newsTypes';

export default {
  name: 'NewsItem',
  props: {
    newsList: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      visibleNewsList: [], // 表示するニュースのリスト
      visibleCount: 5, // 初期表示件数
      isLoadingMore: false, // ローディング中フラグ
      selectedType: null, // 選択中の絞り込みtype（nullは「すべて」）
    };
  },
  computed: {
    hasMoreNews() {
      return this.filteredNewsList && this.visibleNewsList.length < this.filteredNewsList.length;
    },
    sortedNewsList() {
      // 日付順にソートされたリストを返す
      return this.newsList.slice().sort((a, b) => new Date(b.date) - new Date(a.date));
    },
    filteredNewsList() {
      // 選択中のtypeでさらに絞り込む
      if (!this.selectedType) return this.sortedNewsList;
      return this.sortedNewsList.filter(news => (news.type || DEFAULT_NEWS_TYPE) === this.selectedType);
    },
    availableTypes() {
      // 絞り込みタグは常に全typeを表示する
      return NEWS_TYPES;
    },
  },
  methods: {
    getNewsTypeColor,
    toggleFilter(type) {
      this.selectedType = this.selectedType === type ? null : type;
    },
    initializeNews() {
      // filteredNewsList を使って初期表示
      if (this.filteredNewsList) {
        this.visibleNewsList = this.filteredNewsList.slice(0, this.visibleCount);
      }
    },
    showMore() {
      if (!this.filteredNewsList) return;

      this.isLoadingMore = true;
      setTimeout(() => {
        const startIndex = this.visibleNewsList.length;
        const endIndex = startIndex + this.visibleCount;

        for (let i = startIndex; i < endIndex && i < this.filteredNewsList.length; i++) {
          this.visibleNewsList.push(this.filteredNewsList[i]);
        }

        this.isLoadingMore = false;
      }, 300);
    },
    selectNews(news) {
      this.$router.push(`/news/${news.news_id}`);
    }
  },
  watch: {
    newsList: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.initializeNews();
        }
      },
    },
    selectedType() {
      this.initializeNews();
    },
  },
};
</script>

<style scoped>
.news-item {
  padding: 10px 10px 0px 10px;
  margin-bottom: 10px;
  border-bottom: 1px solid #ddd;
}

.news-item .title-date {
  gap: 10px;
  align-items: flex-end;
  margin-bottom: 10px;
  display: flex;
}

.date-tag-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}

.title-date h3 {
  padding-left: 1.5em;
  transition: .15s ease-out;
  -webkit-transform: translateZ(0);
  margin-bottom: 0;
  cursor: pointer;
}

.news-type-tag {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid;
  font-size: 12px;
  font-weight: bold;
  white-space: nowrap;
  box-sizing: border-box;
}

.date-tag-row .news-type-tag {
  min-width: 120px;
  text-align: center;
}

.filter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
}

.filter-tag {
  cursor: pointer;
  opacity: 0.55;
  transition: opacity 0.15s ease, box-shadow 0.15s ease;
  background-color: #f5f5f5;
  color: #616161;
  border-color: #e0e0e0;
}

.filter-tag.active {
  opacity: 1;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.15) inset;
}

.no-news {
  padding: 10px 0;
  color: #666;
}

.title-date p {
  margin-bottom: 0;
}

.title-date small {
  white-space: nowrap;
}

.title-date h3:hover {
  color: #6bafe7;
}

button.load-more {
  display: block;
  margin: 20px auto;
  padding: 10px 20px;
  background: #f8f9fa;
  border: 2px solid #007BFF;
  border-radius: 5px;
  font-size: 15px;
  font-weight: bold;
  line-height: 1.7;
  letter-spacing: 0.06em;
  color: #007BFF;
  cursor: pointer;
  transition: .10s ease-out;
}

button.load-more:disabled {
  background-color: #ccc;
  border-color: #ccc;
  color: #666;
  cursor: not-allowed;
}

button.load-more:hover:not(:disabled) {
  background-color: #007BFF;
  border-color: #007BFF;
  color: #ffffff;
}

@media (max-width: 768px) {
  .news-item .title-date {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }

  .title-date h3 {
    padding-left: 0.1em;
  }
}

</style>
